import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type { VersionEntry, VersionHistoryResponse, VersioningOpResponse } from '@localagent/sdk';

// --- Workspace versioning ---
//
// The version timeline, the unsaved-changes signal and the cross-device drift
// line. All copy derived from these hooks speaks VERSIONING, never git
// — the hooks deliberately expose no raw diff text and no
// commit vocabulary for a component to leak into the DOM.

const VERSIONS_KEY = 'versions';

function versionHistoryKey(workspaceId?: string, sessionId?: string) {
  return ['workspaces', VERSIONS_KEY, workspaceId, sessionId] as const;
}

export function useVersionHistory(workspaceId?: string, sessionId?: string, limit?: number) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<VersionHistoryResponse, Error>({
    queryKey: versionHistoryKey(workspaceId, sessionId),
    queryFn: () =>
      getSDK().workspaces.listHistory(workspaceId!, {
        session_id: sessionId!,
        ...(limit !== undefined ? { limit } : {}),
      }),
    enabled: Boolean(isAuthenticated && sdkConfigured && workspaceId && sessionId),
  });
}

export function countUnsavedChanges(output: Record<string, unknown>): number {
  const status = output['status'];
  if (typeof status !== 'string') {
    throw new Error('unsaved-changes response carried no status text');
  }
  return status.split('\n').filter((line) => line.trim().length > 0).length;
}

/**
 * How many files differ from the last saved version — a COUNT ONLY.
 *
 * The query function derives the number and returns it, so the cache holds an
 * integer rather than the full uncommitted delta. Rendering a badge must not
 * carry the whole working-tree diff body on every refresh, and nothing
 * downstream can accidentally render the raw change text (which would leak git
 * vocabulary into the DOM).
 *
 * The CACHE is count-only, but `viewChanges` still returns the
 * porcelain change listing over the wire, which is discarded here. This is a
 * bounded, one-line-per-changed-file listing (NOT content diffs), so the wire
 * cost is small; a dedicated count-only sandbox route is deferred until badge
 * refresh frequency makes it worthwhile.
 */
export function useUnsavedChanges(workspaceId?: string, sessionId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<number, Error>({
    queryKey: ['workspaces', 'unsaved-changes', workspaceId, sessionId],
    queryFn: async () => {
      const res = await getSDK().workspaces.viewChanges(workspaceId!, sessionId!);
      return countUnsavedChanges(res.output);
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && workspaceId && sessionId),
  });
}

/**
 * Top-level workspace paths, offered by the restore dialog's optional file
 * picker (cloud_filesystem ADR-007). Folders are legal restore targets, so a root
 * listing covers the whole tree at folder granularity plus the root files —
 * without walking the tree over N round trips.
 */
export function useWorkspaceRootPaths(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<string[], Error>({
    queryKey: ['workspaces', 'root-paths', workspaceId],
    queryFn: async () => {
      const res = await getSDK().workspaces.executeFileOp(workspaceId!, {
        op_type: 'list',
        // Workspace-relative, never absolute: the sandbox path guard
        // canonicalizes and rejects "/" as resolving outside the workspace root.
        path: '.',
      });
      if (res.error) {
        throw new Error(res.error);
      }
      const entries = res.metadata['entries'];
      if (!Array.isArray(entries)) {
        throw new Error('workspace listing returned no entries');
      }
      return entries
        .map((entry) => (entry as { path?: unknown }).path)
        .filter((path): path is string => typeof path === 'string')
        .sort((a, b) => a.localeCompare(b));
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && workspaceId),
  });
}

export interface RestoreVersionVariables {
  sessionId: string;
  version: string;
  message?: string;
  paths?: string[];
}

export function useRestoreVersion(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<VersioningOpResponse, Error, RestoreVersionVariables>({
    mutationFn: async ({ sessionId, version, message, paths }) => {
      const response = await getSDK().workspaces.restoreVersion(workspaceId!, {
        session_id: sessionId,
        version,
        ...(message !== undefined ? { message } : {}),
        ...(paths !== undefined ? { paths } : {}),
      });
      const newVersion = response.output['version'];
      const restoredFrom = response.output['restored_from'];
      if (typeof newVersion !== 'string' || typeof restoredFrom !== 'string') {
        throw new Error('restore response carried no version id');
      }
      return response;
    },
    onSuccess: (response, variables) => {
      if (!isAuthenticated) return;

      const newVersion = response.output['version'] as string;
      const committedMessage = response.output['message'];
      const head: VersionEntry = {
        version: newVersion,
        message:
          typeof committedMessage === 'string' ? committedMessage : (variables.message ?? ''),
        timestamp: new Date().toISOString(),
      };

      queryClient.setQueryData<VersionHistoryResponse>(
        versionHistoryKey(workspaceId, variables.sessionId),
        (prev) => {
          const existing = prev?.entries ?? [];

          if (existing.some((e) => e.version === newVersion)) {
            return {
              status: prev?.status ?? 'ok',
              entries: existing,
              ...(prev?.error !== undefined ? { error: prev.error } : {}),
            };
          }

          return {
            status: prev?.status ?? 'ok',
            entries: [head, ...existing],
            ...(prev?.error !== undefined ? { error: prev.error } : {}),
          };
        }
      );

      queryClient.setQueryData<number>(
        ['cloud-filesystem', 'unsaved-changes', workspaceId, variables.sessionId],
        0
      );
    },
  });
}
